import { type ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import XeroMetricInfo, {
  type MetricDefinition,
} from "@/components/admin/XeroMetricInfo";


export const SCROLLER =
  "max-h-[calc(100vh-22rem)] min-h-[8rem] overflow-auto rounded-lg border border-border";

interface Props {
  title: string;
  /** One line under the heading. Keep it to what the panel is for. */
  blurb?: ReactNode;
  metrics: MetricDefinition[];
  /** Rendered beside the info icon — a token countdown, a computed-at stamp. */
  aside?: ReactNode;
  onRefresh?: () => void;
  isFetching?: boolean;
  children: ReactNode;
}

export default function XeroPanel({
  title,
  blurb,
  metrics,
  aside,
  onRefresh,
  isFetching = false,
  children,
}: Props) {
  return (
    <section className="flex flex-col">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-medium text-foreground">{title}</h3>
            <XeroMetricInfo title={title} metrics={metrics} />
          </div>
          {blurb && <p className="text-xs text-muted-foreground">{blurb}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {aside}
          {onRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isFetching}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {children}
    </section>
  );
}
